import ListingCardTypeEnum from '@models/enums/ListingCardTypeEnum';
import { IProduct } from '@models/IProduct';
import { ListingProductType } from '@models/IProductListing';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { IPromoCard } from '@models/IPromoCard';
import ProductListingCard from '@molecules/ProductListingCard/ProductListingCard';
import PromoCard from '@molecules/PromoCard/PromoCard';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useRefocus from '@utils/hooks/useRefocus';
import { useInView } from 'framer-motion';
import { useEffect, useRef } from 'react';

interface Props {
  readonly activeCard: IProduct | null;
  readonly activeColumnIndex: number | null;
  readonly activeRowIndex: number | null;
  readonly chunkedResults: ListingProductType[][];
  readonly nextFocusIndex: number;
  readonly products: ListingProductType[];
  readonly productListingName?: string;
  readonly rowIndex: number;
  readonly statusLabels: IProductListingStatusLabels;
  readonly onDesktopKitDropdownButtonClick: (
    rowIndex: number,
    columnIndex: number
  ) => void;
  readonly onMobileKitDropdownButtonClick: (rowIndex: number) => void;
  readonly onListingProductsTracking: (products: ListingProductType[]) => void;
}

const ProductListingCardRow = ({
  activeCard,
  activeColumnIndex,
  activeRowIndex,
  chunkedResults,
  nextFocusIndex,
  products,
  productListingName,
  rowIndex,
  statusLabels,
  onDesktopKitDropdownButtonClick,
  onMobileKitDropdownButtonClick,
  onListingProductsTracking,
}: Props) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRefocus(chunkedResults);
  const isMobile = useMediaQuery(1024);
  const isRowInView = useInView(rowRef, { amount: 0.5 });

  useEffect(() => {
    if (isRowInView) onListingProductsTracking(products);
  }, [isRowInView, onListingProductsTracking, products]);

  return (
    <div
      ref={rowRef}
      className="container grid gap-5 bg-black-100 lg:grid-cols-12"
    >
      {isMobile
        ? products[0] && (
            <div className="col-span-full">
              {products[0].type === ListingCardTypeEnum.Product ? (
                <ProductListingCard
                  {...products[0]}
                  statusLabels={statusLabels}
                  onKitDropdownButtonClick={() => {
                    onMobileKitDropdownButtonClick(rowIndex);
                  }}
                  active={activeRowIndex === rowIndex}
                  productListingName={productListingName}
                />
              ) : (
                <PromoCard {...(products[0] as IPromoCard)} />
              )}
            </div>
          )
        : products.map((product, columnIndex) => (
            <div
              tabIndex={-1}
              ref={
                nextFocusIndex > 0 &&
                nextFocusIndex === 3 * rowIndex + columnIndex
                  ? placeholderRef
                  : null
              }
              key={product.title}
              className="col-span-4"
            >
              {products[columnIndex] &&
                product.type === ListingCardTypeEnum.Product && (
                  <ProductListingCard
                    {...product}
                    statusLabels={statusLabels}
                    onKitDropdownButtonClick={() => {
                      onDesktopKitDropdownButtonClick(rowIndex, columnIndex);
                    }}
                    active={
                      activeColumnIndex === columnIndex &&
                      activeRowIndex === rowIndex &&
                      !!activeCard
                    }
                    productListingName={productListingName}
                  />
                )}
              {products[columnIndex] &&
                product.type === ListingCardTypeEnum.Promo && (
                  <PromoCard {...(product as IPromoCard)} />
                )}
            </div>
          ))}
    </div>
  );
};

export default ProductListingCardRow;
